import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Loader2, CheckCircle2, Upload, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    listingLink: '',
    monthlyRent: '',
    description: ''
  });

  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 0) {
      setSelectedImages(prev => [...prev, ...files]);
      
      // Create previews
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Create FormData to handle file uploads
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('listingLink', formData.listingLink || '');
      submitData.append('monthlyRent', formData.monthlyRent);
      submitData.append('description', formData.description);
      
      // Append images
      selectedImages.forEach((image, index) => {
        submitData.append(`images`, image);
      });

      const response = await axios.post(
        `${BACKEND_URL}/api/property-submission`, 
        submitData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data) {
        setSubmitSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            listingLink: '',
            monthlyRent: '',
            description: ''
          });
          setSelectedImages([]);
          setImagePreviews([]);
          setSubmitSuccess(false);
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Si è verificato un errore. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <Card className="p-12 bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Richiesta Inviata!</h3>
        <p className="text-slate-700">
          Grazie per il tuo interesse. Il nostro team valuterà la tua richiesta e ti contatterà presto.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-8 md:p-12 bg-white border-slate-200 shadow-xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-900 font-medium">
              Nome e Cognome *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="Mario Rossi"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-900 font-medium">
              Email *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="mario.rossi@email.com"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-slate-900 font-medium">
              Telefono *
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="+39 123 456 7890"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="monthlyRent" className="text-slate-900 font-medium">
              Canone Mensile (€) *
            </Label>
            <Input
              id="monthlyRent"
              name="monthlyRent"
              type="number"
              required
              value={formData.monthlyRent}
              onChange={handleChange}
              className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="750"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="listingLink" className="text-slate-900 font-medium">
            Link Annuncio (se esiste)
          </Label>
          <Input
            id="listingLink"
            name="listingLink"
            type="url"
            value={formData.listingLink}
            onChange={handleChange}
            className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="https://..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description" className="text-slate-900 font-medium">
            Breve Descrizione Immobile *
          </Label>
          <Textarea
            id="description"
            name="description"
            required
            value={formData.description}
            onChange={handleChange}
            rows={5}
            className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
            placeholder="Descrivi il tuo immobile: tipologia, numero di stanze, caratteristiche principali, zona..."
          />
        </div>

        {/* Image Upload Section */}
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <Label htmlFor="images" className="text-slate-900 font-medium">
                Foto dell'Immobile
              </Label>
              <p className="text-sm text-slate-600 mt-1">
                Carica alcune foto dell'immobile (consigliato per ricevere richieste più in linea)
              </p>
            </div>
          </div>

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-emerald-500 transition-colors">
            <label htmlFor="images" className="cursor-pointer block">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-emerald-600" />
                </div>
                <div className="text-center">
                  <p className="text-slate-900 font-medium">
                    Click per caricare le foto
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Formati supportati: JPG, PNG (max 5MB per foto)
                  </p>
                </div>
              </div>
              <input
                id="images"
                name="images"
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                multiple
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          </div>

          {/* Image Previews */}
          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border-2 border-slate-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-slate-500 italic">
            💡 Puoi inviarle anche successivamente
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Invio in corso...
              </>
            ) : (
              'Richiedi Inserimento Gratuito'
            )}
          </Button>
          
          <p className="text-center text-sm text-slate-500 mt-4">
            Le richieste vengono approvate manualmente per garantire qualità.
          </p>
        </div>
      </form>
    </Card>
  );
};

export default PropertyForm;
